import { Query } from '../../../generated/graphql';
import { SmsLinkSignIn } from './SmsLinkSignIn';
import { EmailLinkSignIn } from './EmailLinkSignIn';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  Spinner,
  Center,
  Text,
  Button,
  Divider,
  HStack,
  useColorMode,
} from '@chakra-ui/react';
import { FiKey, FiPhone, FiMail, FiUser } from 'react-icons/fi';
import { useQuery, useMutation } from '@apollo/client';
import { getColor, getComponent, brandConfig } from '../../../brandConfig';
import { CaptureUserDetailsModal } from './CaptureUserDetailsModal';
import { useAuthState, usePhoneInput, useAuthVerification, useUsernameAuth } from '../hooks';
import { PhoneInputForm } from './PhoneInputForm';
import { EmailInputForm } from './EmailInputForm';
import { CodeVerificationForm } from './CodeVerificationForm';
import { UsernamePasswordForm } from './UsernamePasswordForm';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { NoAccountFoundCard } from './NoAccountFoundCard';
import { AccessBlockedCard } from './AccessBlockedCard';
import { isAccessBlockedError } from '../types';
import { COUNTRY_CODES, type AuthMethod } from '../types';
import {
  GET_TENANT_AUTH_CONFIG,
  GENERATE_PASSKEY_AUTHENTICATION_OPTIONS,
  VERIFY_PASSKEY_AUTHENTICATION,
} from '../queries';
import { loginWithPasskey, browserSupportsWebAuthn, friendlyPasskeyError } from '../utils/passkey';
import { isLocalhost, currentHost } from '../utils/passkeyEnv';

interface UnifiedLoginModalProps {
  returnPath?: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (token: string) => void;
  // Legacy prop names for backward compatibility
  onLoginSuccess?: () => void;
  /**
   * When true, skip the post-login CaptureUserDetailsModal. Used by public funnels
   * (e.g. P2 quick-checkout) that handle profile capture themselves. Optional — the
   * canonical modal supports it so every tenant caller compiles.
   */
  skipCaptureDetails?: boolean;
  /**
   * Escape-hatch for public funnels (e.g. P2 quick-checkout) where unknown
   * email/phone should auto-signup instead of showing NoAccountFoundCard.
   * When provided AND the verify step returns LOOKUP_FAILED, this callback
   * is invoked INSTEAD of rendering NoAccountFoundCard. The callback owns
   * the create-and-log-in UX (calling its own dedicated public mutation,
   * storing the token, refreshing auth, closing the modal).
   */
  onAccountNotFound?: (info: {
    method: 'phone' | 'email' | 'username';
    identifier: string; // phone or email or username
    code?: string;      // verification code if applicable
  }) => Promise<void> | void;
  /**
   * Open straight on the AccessBlockedCard (2026-09-04). /login sets this from
   * `?blocked=1`, where the Apollo error link sends someone whose EXISTING session
   * was refused with ACCESS_BLOCKED (their token is already cleared). Optional.
   */
  initialAccessBlocked?: boolean;
}

/**
 * Unified Login Modal Component
 *
 * Provides passwordless authentication via:
 * - SMS verification (preferred for Australian mobile numbers)
 * - Email verification (fallback option)
 *
 * Features:
 * - Country-specific phone formatting
 * - Automatic user details capture for incomplete profiles
 * - Dark mode support
 * - Accessibility compliant
 *
 * @example
 * ```tsx
 * <UnifiedLoginModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   onSuccess={(token) => {
 *     localStorage.setItem('auth_token', token);
 *     navigate('/dashboard');
 *   }}
 * />
 * ```
 */
export const UnifiedLoginModal: React.FC<UnifiedLoginModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onLoginSuccess, // Legacy prop for backward compatibility
  onAccountNotFound,
  skipCaptureDetails,
  returnPath,
  initialAccessBlocked,
}) => {
  // Fetch tenant auth configuration
  const { data: tenantData, loading: configLoading, error: configError } = useQuery<Pick<Query, 'tenantAuthConfig'>>(GET_TENANT_AUTH_CONFIG, {
    fetchPolicy: 'cache-first',
  });

  // Track whether we've applied the tenant config at least once
  const configApplied = useRef(false);

  // Extract auth config with defaults
  // Note: tenantAuthConfig is a public query that returns the config directly (not nested under currentTenant)
  const authConfig = useMemo(() => ({
    enablePhoneAuth: tenantData?.tenantAuthConfig?.enablePhoneAuth ?? true,
    enableEmailAuth: tenantData?.tenantAuthConfig?.enableEmailAuth ?? true,
    enableUsernamePassword: tenantData?.tenantAuthConfig?.enableUsernamePassword ?? false,
    enablePasskeyAuth: tenantData?.tenantAuthConfig?.enablePasskeyAuth ?? false,
    defaultAuthMethod: (tenantData?.tenantAuthConfig?.defaultAuthMethod || 'phone') as AuthMethod,
    discreteAlternativeAuth: tenantData?.tenantAuthConfig?.discreteAlternativeAuth ?? false,
  }), [tenantData]);

  // Passkey (WebAuthn) login
  const [generatePasskeyAuthOptions] = useMutation(GENERATE_PASSKEY_AUTHENTICATION_OPTIONS);
  const [verifyPasskeyAuth] = useMutation(VERIFY_PASSKEY_AUTHENTICATION);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyError, setPasskeyError] = useState('');

  // Color mode support
  const { colorMode } = useColorMode();

  // Define theme-aware colors
  const cardBg = getColor(colorMode === 'light' ? "background.card" : "background.darkSurface", colorMode);
  const textPrimary = getColor(
    colorMode === 'light' ? "text.primary" : "text.primaryDark",
    colorMode
  );
  const textSecondary = getColor(
    colorMode === 'light' ? "text.secondary" : "text.secondaryDark",
    colorMode
  );
  const textMuted = getColor(
    colorMode === 'light' ? "text.muted" : "text.mutedDark",
    colorMode
  );
  // Use the tenant's brandConfig.colors.primary directly — getColor("primary")
  // walks lightTheme/darkTheme.primary first which still hold the legacy platform
  // blue (#007AFF). brandConfig.colors.primary is the per-tenant brand colour
  // (Anna = forest green #2D5A27).
  const accentBlue = brandConfig.colors.primary;
  const accentHover = brandConfig.colors.primaryHover;

  // Form component colors
  const formFieldBg = getComponent("form", "fieldBg", colorMode);
  const formFieldBorder = getComponent("form", "fieldBorder", colorMode);
  const formFieldBorderFocus = getComponent("form", "fieldBorderFocus", colorMode);
  const formLabelColor = getComponent("form", "labelColor", colorMode);

  // Authentication state management
  const {
    authMethod,
    authStep,
    selectedCountry,
    phoneNumber,
    localPhoneNumber,
    email,
    username,
    password,
    confirmPassword,
    isSignup,
    code,
    error,
    showCaptureDetails,
    verifiedUserId,
    verifiedUserData,
    pendingToken,
    lookupFailedInfo,
    accessBlockedInfo,
    setAuthStep,
    setSelectedCountry,
    setLocalPhoneNumber,
    setPhoneNumber,
    setEmail,
    setUsername,
    setPassword,
    setConfirmPassword,
    setIsSignup,
    setCode,
    setError,
    setShowCaptureDetails,
    setVerifiedUserId,
    setVerifiedUserData,
    setPendingToken,
    setLookupFailedInfo,
    setAccessBlockedInfo,
    resetState,
    switchToEmail,
    switchToPhone,
    switchToUsername,
    goToForgotPassword,
    goBackToLogin,
  } = useAuthState();

  // Phone input handling
  const { handlePhoneInput } = usePhoneInput(
    selectedCountry,
    setLocalPhoneNumber,
    setPhoneNumber
  );

  // Handle both new and legacy callback signatures
  const handleSuccess = (token: string) => {
    console.log('[FRONTEND-2] 📝 Token length:', token?.length);

    if (onSuccess) {
      console.log('[FRONTEND-3] 🔄 Calling onSuccess callback');
      onSuccess(token);
    } else if (onLoginSuccess) {
      console.log('[FRONTEND-3] 💾 Using legacy onLoginSuccess - storing in localStorage');
      localStorage.setItem('auth_token', token);

      // Verify it was stored
      const storedToken = localStorage.getItem('auth_token');
      console.log('[FRONTEND-4] ✅ Verification - token stored in localStorage as "auth_token":', !!storedToken);
      console.log('[FRONTEND-5] ✅ Stored token matches:', storedToken === token);

      console.log('[FRONTEND-6] 📞 Calling onLoginSuccess callback');
      onLoginSuccess();
    } else {
      console.error('[FRONTEND-3] ❌ No callback provided!');
    }
  };

  // Sign in with a passkey (usernameless WebAuthn). On success, mint + hand back the JWT
  // exactly like the SMS/email/username flows do.
  const handlePasskeyLogin = async () => {
    setPasskeyError('');
    setPasskeyLoading(true);
    try {
      const token = await loginWithPasskey(generatePasskeyAuthOptions, verifyPasskeyAuth);
      handleSuccess(token);
    } catch (err: any) {
      if (isAccessBlockedError(err)) {
        // The passkey verified; the account's sign-in is switched off. Card, not a toast.
        setAccessBlockedInfo({ method: 'passkey' });
        setAuthStep('access-blocked');
        return;
      }
      setPasskeyError(friendlyPasskeyError(err));
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Username/password authentication
  const {
    usernameAvailable,
    checkingUsername,
    loading: usernameAuthLoading,
    checkUsernameAvailability,
    cancelUsernameCheck,
    handleSubmit: handleUsernameSubmit,
  } = useUsernameAuth({
    username,
    password,
    confirmPassword,
    isSignup,
    setError,
    setVerifiedUserId,
    setVerifiedUserData,
    setPendingToken,
    setShowCaptureDetails,
    setAccessBlockedInfo,
    setAuthStep,
    onSuccess: handleSuccess,
    onClose: handleClose,
  });

  // Set default auth method based on tenant config when modal opens
  useEffect(() => {
    console.log('🔐 [AUTH-CONFIG] Modal open:', isOpen);
    console.log('🔐 [AUTH-CONFIG] Auth config:', authConfig);

    if (!isOpen) {
      // Reset the flag when modal closes so it re-applies on next open
      configApplied.current = false;
      return;
    }

    if (isOpen && tenantData?.tenantAuthConfig) {
      const { defaultAuthMethod, enablePhoneAuth, enableEmailAuth, enableUsernamePassword } = authConfig;

      console.log('🔐 [AUTH-CONFIG] Default method:', defaultAuthMethod);
      console.log('🔐 [AUTH-CONFIG] Enabled - Phone:', enablePhoneAuth, 'Email:', enableEmailAuth, 'Username:', enableUsernamePassword);

      // Find the first enabled method to use as default
      let methodToUse: AuthMethod = 'phone';

      if (defaultAuthMethod === 'username' && enableUsernamePassword) {
        methodToUse = 'username';
      } else if (defaultAuthMethod === 'email' && enableEmailAuth) {
        methodToUse = 'email';
      } else if (defaultAuthMethod === 'phone' && enablePhoneAuth) {
        methodToUse = 'phone';
      } else if (enableUsernamePassword) {
        methodToUse = 'username';
      } else if (enableEmailAuth) {
        methodToUse = 'email';
      } else if (enablePhoneAuth) {
        methodToUse = 'phone';
      }

      console.log('🔐 [AUTH-CONFIG] Method to use:', methodToUse, 'Current method:', authMethod);

      // Apply the config method
      if (methodToUse !== authMethod) {
        if (methodToUse === 'phone') switchToPhone();
        else if (methodToUse === 'email') switchToEmail();
        else if (methodToUse === 'username') switchToUsername();
      }

      configApplied.current = true;
    }
  }, [isOpen, tenantData, authConfig]);

  // Auto-complete login when CaptureUserDetailsModal would be a no-op
  // (client has name already, only missing optional fields like email)
  useEffect(() => {
    if (showCaptureDetails && verifiedUserData?.fName && verifiedUserData?.lName && pendingToken) {
      console.log('⚡ Client already has name — skipping capture modal, proceeding with login');
      setShowCaptureDetails(false);
      handleSuccess(pendingToken);
      handleClose();
    }
  }, [showCaptureDetails, verifiedUserData, pendingToken]);

  // Auto-signup escape hatch (P2 quick-checkout funnel & friends).
  // When the parent provides onAccountNotFound AND we hit the no-account state,
  // fire the callback once and surface a spinner. The callback owns the create
  // + log in + close-modal flow. If it throws we fall through to the standard
  // NoAccountFoundCard.
  const [autoSignupState, setAutoSignupState] = useState<'idle' | 'pending' | 'failed'>('idle');
  const autoSignupFiredRef = useRef(false);

  useEffect(() => {
    if (authStep !== 'no-account') {
      autoSignupFiredRef.current = false;
      if (autoSignupState !== 'idle') setAutoSignupState('idle');
      return;
    }
    if (!onAccountNotFound || !lookupFailedInfo || autoSignupFiredRef.current) return;
    autoSignupFiredRef.current = true;
    setAutoSignupState('pending');
    Promise.resolve(
      onAccountNotFound({
        method: lookupFailedInfo.method,
        identifier: lookupFailedInfo.identifier,
        // Use the code captured on lookupFailedInfo — the `code` field state is cleared
        // when we switch to the no-account view, so it would be empty here otherwise.
        code: lookupFailedInfo.code,
      })
    )
      .then(() => {
        // Parent is responsible for closing the modal on success.
      })
      .catch((err) => {
        console.error('[UnifiedLoginModal] onAccountNotFound failed:', err);
        setAutoSignupState('failed');
      });
  }, [authStep, lookupFailedInfo, onAccountNotFound, code, autoSignupState]);

  // Check username availability when username changes (signup only)
  useEffect(() => {
    if (isSignup && username.length >= 3) {
      checkUsernameAvailability(username);
    }
    return () => {
      cancelUsernameCheck();
    };
  }, [username, isSignup, checkUsernameAvailability, cancelUsernameCheck]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const country = COUNTRY_CODES.find(c => c.code === e.target.value);
    if (country) {
      setSelectedCountry(country);
      // Reformat existing number for new country
      if (localPhoneNumber) {
        handlePhoneInput(localPhoneNumber);
      }
    }
  };

  // Verification handlers
  const {
    handleSendCode,
    handleVerifyCode,
    handleResendCode,
    loading,
  } = useAuthVerification({
    authMethod,
    phoneNumber,
    localPhoneNumber,
    email,
    code,
    selectedCountry,
    setAuthStep,
    setError,
    setCode,
    setVerifiedUserId,
    setVerifiedUserData,
    setPendingToken,
    setShowCaptureDetails,
    setLookupFailedInfo,
    setAccessBlockedInfo,
    onSuccess: handleSuccess,
    onClose: handleClose,
  });

  // /login?blocked=1 — a refused EXISTING session landed here; open on the card.
  useEffect(() => {
    if (isOpen && initialAccessBlocked) {
      setAccessBlockedInfo({ method: 'session' });
      setAuthStep('access-blocked');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialAccessBlocked]);

  // Alternative sign-in methods offered ABOVE the text fields (the current
  // method's form renders below). Baby-boomer-friendly: each is a large,
  // high-contrast, plain-language button with an icon. We surface every
  // *enabled* method except the one already showing below, so the visitor
  // can pick another way to sign in before ever tapping a text field (and
  // before the on-screen keyboard opens over the lower half of the screen).
  const alternativeMethods = [
    authConfig.enablePhoneAuth && authMethod !== 'phone'
      ? { key: 'phone', label: 'Use my phone number', icon: <FiPhone />, onClick: switchToPhone }
      : null,
    authConfig.enableEmailAuth && authMethod !== 'email'
      ? { key: 'email', label: 'Use my email address', icon: <FiMail />, onClick: switchToEmail }
      : null,
    authConfig.enableUsernamePassword && authMethod !== 'username'
      ? { key: 'username', label: 'Use username & password', icon: <FiUser />, onClick: switchToUsername }
      : null,
  ].filter(Boolean) as Array<{ key: string; label: string; icon: React.ReactElement; onClick: () => void }>;

  const PhoneSignInForm = tenantData?.tenantAuthConfig?.phoneSignInMode === 'link' ? SmsLinkSignIn : PhoneInputForm;
  const showPasskeyOption = authConfig.enablePasskeyAuth && browserSupportsWebAuthn();
  const hasTopOptions = showPasskeyOption || alternativeMethods.length > 0;

  return (
    <>
      {/* closeOnOverlayClick / closeOnEsc disabled: clicking the page behind the modal
          (or pressing Esc) used to dismiss it mid-entry and lose the typed code. Only the
          X button closes it now.

          Mobile keyboard protection (Molly 2026-07-07): full-screen on base +
          scrollBehavior="inside" + a dvh cap make the body the scroll container
          within the *visual* viewport, so when the on-screen keyboard halves the
          screen every field/link below the fold stays reachable by scrolling.
          onFocusCapture re-centres the focused input once the keyboard starts
          opening (iOS Safari shrinks only the visual viewport, so the browser's
          native scroll-into-view needs the scrollable body to work with). */}
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size={{ base: "full", md: "md" }}
        scrollBehavior="inside"
        closeOnOverlayClick={false}
        closeOnEsc={false}
      >
        <ModalOverlay />
        <ModalContent
          bg={cardBg}
          color={textPrimary}
          maxH={{ base: "100dvh", md: "calc(100dvh - 4rem)" }}
          onFocusCapture={(e) => {
            const t = e.target as HTMLElement;
            if (t && ["INPUT", "SELECT", "TEXTAREA"].includes(t.tagName)) {
              window.setTimeout(() => {
                t.scrollIntoView({ block: "center", behavior: "smooth" });
              }, 150);
            }
          }}
        >
          <ModalHeader color={textPrimary}>
            {authStep === 'input'
              ? authMethod === 'username'
                ? isSignup
                  ? 'Create Account'
                  : 'Sign In'
                : 'Sign In'
              : authStep === 'verify'
              ? 'Verify Code'
              : authStep === 'forgot-password'
              ? 'Reset Password'
              : authStep === 'no-account'
              ? 'Account not found'
              : authStep === 'access-blocked'
              ? 'Sign-in switched off'
              : 'Sign In'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {/* Wait for tenant auth config before rendering form to prevent wrong method flash.
                Fall through on error so we still show a form with defaults rather than a stuck spinner. */}
            {(configLoading && !configApplied.current && !configError) ? (
              <Center py={8}>
                <Spinner size="lg" color={accentBlue} />
              </Center>
            ) : (
            <VStack spacing={4} align="stretch">
              {authStep === 'input' && (
                <>
                  {/* ── Alternative sign-in options, pinned to the TOP ──────────────
                      These sit ABOVE the email/username/password fields on purpose:
                      the mobile on-screen keyboard only opens when a text field is
                      focused, so keeping every alternative-login action up here means
                      the keyboard can never cover them. Baby-boomer-friendly — large
                      tap targets, high-contrast, icons + plain-language labels. */}
                  {hasTopOptions && (
                    <VStack spacing={3} align="stretch">
                      {showPasskeyOption && (
                        <Button
                          leftIcon={<FiKey size={22} />}
                          bg={accentBlue}
                          color="white"
                          _hover={{ bg: accentHover }}
                          _active={{ bg: accentHover }}
                          width="100%"
                          minH="60px"
                          fontSize="lg"
                          fontWeight="bold"
                          borderRadius="xl"
                          onClick={handlePasskeyLogin}
                          isLoading={passkeyLoading}
                          loadingText="Waiting for passkey…"
                        >
                          Sign in with a passkey
                        </Button>
                      )}

                      {alternativeMethods.map((m) => (
                        <Button
                          key={m.key}
                          leftIcon={m.icon}
                          variant="outline"
                          width="100%"
                          minH="56px"
                          fontSize="md"
                          fontWeight="semibold"
                          borderRadius="xl"
                          borderWidth="2px"
                          borderColor={accentBlue}
                          color={textPrimary}
                          _hover={{ bg: `${accentBlue}14` }}
                          _active={{ bg: `${accentBlue}22` }}
                          onClick={m.onClick}
                        >
                          {m.label}
                        </Button>
                      ))}

                      {passkeyError && (
                        <Text fontSize="sm" color="red.400" textAlign="center">{passkeyError}</Text>
                      )}
                      {showPasskeyOption && isLocalhost() && (
                        <Text fontSize="xs" color={textMuted} textAlign="center">
                          Dev mode: only passkeys you created here ({currentHost()}) will appear — your live-site passkeys won't.
                        </Text>
                      )}

                      <HStack pt={1}>
                        <Divider />
                        <Text fontSize="sm" color={textMuted} whiteSpace="nowrap" px={1}>
                          or enter your details
                        </Text>
                        <Divider />
                      </HStack>
                    </VStack>
                  )}

                  {authMethod === 'phone' ? (
                    <PhoneSignInForm
                      returnPath={returnPath || (window.location.pathname === '/login' ? '/' : `${window.location.pathname}${window.location.search}${window.location.hash}`)}
                      selectedCountry={selectedCountry}
                      localPhoneNumber={localPhoneNumber}
                      phoneNumber={phoneNumber}
                      error={error}
                      loading={loading}
                      formLabelColor={formLabelColor}
                      formFieldBg={formFieldBg}
                      formFieldBorder={formFieldBorder}
                      formFieldBorderFocus={formFieldBorderFocus}
                      textPrimary={textPrimary}
                      textSecondary={textSecondary}
                      textMuted={textMuted}
                      accentBlue={accentBlue}
                      accentHover={accentHover}
                      onCountryChange={handleCountryChange}
                      onPhoneInput={handlePhoneInput}
                      onSendCode={handleSendCode}
                    />
                  ) : authMethod === 'email' ? (
                    ['link', 'link_and_code'].includes(tenantData?.tenantAuthConfig?.emailSignInMode || '') ? <EmailLinkSignIn allowSignup={tenantData?.tenantAuthConfig?.allowEmailLinkSignup ?? false} presentation="embedded" flow={tenantData?.tenantAuthConfig?.emailLinkFlow} returnPath={returnPath || (window.location.pathname === '/login' ? '/' : `${window.location.pathname}${window.location.search}${window.location.hash}`)} /> : (
                    <EmailInputForm
                      email={email}
                      error={error}
                      loading={loading}
                      formLabelColor={formLabelColor}
                      formFieldBg={formFieldBg}
                      formFieldBorder={formFieldBorder}
                      formFieldBorderFocus={formFieldBorderFocus}
                      textPrimary={textPrimary}
                      textSecondary={textSecondary}
                      textMuted={textMuted}
                      accentBlue={accentBlue}
                      accentHover={accentHover}
                      onEmailChange={setEmail}
                      onSendCode={handleSendCode}
                    />
                    )
                  ) : (
                    <UsernamePasswordForm
                      username={username}
                      password={password}
                      confirmPassword={confirmPassword}
                      isSignup={isSignup}
                      error={error}
                      loading={usernameAuthLoading}
                      usernameAvailable={usernameAvailable}
                      checkingUsername={checkingUsername}
                      discreteAlternativeAuth={authConfig.discreteAlternativeAuth}
                      formLabelColor={formLabelColor}
                      formFieldBg={formFieldBg}
                      formFieldBorder={formFieldBorder}
                      formFieldBorderFocus={formFieldBorderFocus}
                      textPrimary={textPrimary}
                      textSecondary={textSecondary}
                      textMuted={textMuted}
                      accentBlue={accentBlue}
                      accentHover={accentHover}
                      onUsernameChange={setUsername}
                      onPasswordChange={setPassword}
                      onConfirmPasswordChange={setConfirmPassword}
                      onToggleMode={() => setIsSignup(!isSignup)}
                      onSubmit={handleUsernameSubmit}
                      onForgotPassword={goToForgotPassword}
                    />
                  )}
                </>
              )}

              {authStep === 'verify' && (
                <CodeVerificationForm
                  authMethod={authMethod}
                  phoneNumber={phoneNumber}
                  email={email}
                  code={code}
                  error={error}
                  loading={loading}
                  formFieldBg={formFieldBg}
                  formFieldBorder={formFieldBorder}
                  formFieldBorderFocus={formFieldBorderFocus}
                  textPrimary={textPrimary}
                  textMuted={textMuted}
                  accentBlue={accentBlue}
                  accentHover={accentHover}
                  onCodeChange={setCode}
                  onVerifyCode={handleVerifyCode}
                  onResendCode={handleResendCode}
                  onChangeIdentifier={() => setAuthStep('input')}
                />
              )}

              {authStep === 'forgot-password' && (
                <ForgotPasswordForm
                  formLabelColor={formLabelColor}
                  formFieldBg={formFieldBg}
                  formFieldBorder={formFieldBorder}
                  formFieldBorderFocus={formFieldBorderFocus}
                  textPrimary={textPrimary}
                  textSecondary={textSecondary}
                  textMuted={textMuted}
                  accentBlue={accentBlue}
                  accentHover={accentHover}
                  onBack={goBackToLogin}
                  onSuccess={() => {
                    // After successful reset, go back to login
                    goBackToLogin();
                  }}
                />
              )}

              {authStep === 'no-account' && lookupFailedInfo && onAccountNotFound && autoSignupState === 'pending' && (
                <Center py={8} flexDirection="column" gap={3}>
                  <Spinner size="lg" color={accentBlue} />
                  <Text color={textSecondary} fontSize="sm" textAlign="center">
                    Setting up your account…
                  </Text>
                </Center>
              )}

              {authStep === 'no-account' && lookupFailedInfo && (!onAccountNotFound || autoSignupState === 'failed') && (
                <NoAccountFoundCard
                  info={lookupFailedInfo}
                  textPrimary={textPrimary}
                  textSecondary={textSecondary}
                  textMuted={textMuted}
                  accentBlue={accentBlue}
                  onTryDifferent={() => {
                    setLookupFailedInfo(null);
                    setError('');
                    setAuthStep('input');
                  }}
                  alternativeMethod={
                    lookupFailedInfo.method === 'phone'
                      ? authConfig.enableEmailAuth
                        ? 'email'
                        : authConfig.enableUsernamePassword
                        ? 'username'
                        : undefined
                      : authConfig.enablePhoneAuth
                      ? 'phone'
                      : authConfig.enableUsernamePassword
                      ? 'username'
                      : undefined
                  }
                  onSwitchMethod={(method) => {
                    setLookupFailedInfo(null);
                    setError('');
                    if (method === 'phone') switchToPhone();
                    else if (method === 'email') switchToEmail();
                    else if (method === 'username') switchToUsername();
                  }}
                />
              )}

              {authStep === 'access-blocked' && accessBlockedInfo && (
                <AccessBlockedCard
                  info={accessBlockedInfo}
                  textPrimary={textPrimary}
                  textSecondary={textSecondary}
                  textMuted={textMuted}
                  accentBlue={accentBlue}
                  onTryDifferent={() => {
                    setAccessBlockedInfo(null);
                    setError('');
                    setAuthStep('input');
                  }}
                />
              )}
            </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Capture User Details Modal - shown when user is missing fname/lname/email/phone */}
      {!skipCaptureDetails && showCaptureDetails && verifiedUserId && (
        <CaptureUserDetailsModal
          isOpen={showCaptureDetails}
          onClose={() => {
            setShowCaptureDetails(false);
            setVerifiedUserId(null);
            setVerifiedUserData(null);
            setPendingToken(null);
          }}
          userId={verifiedUserId}
          pendingToken={pendingToken}
          currentUserData={verifiedUserData}
          onUpdateSuccess={() => {
            console.log('📝 User details captured successfully');
            setShowCaptureDetails(false);
            // Now complete the login with the pending token
            if (pendingToken) {
              handleSuccess(pendingToken);
            }
            handleClose();
          }}
        />
      )}
    </>
  );
};
